import { ListItem, ListItemButton, ListItemText, Stack } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'

import { isInHaltingStage } from 'components/attacks/utils/attack-stages'
import AttackTypeIcon from 'components/common/AttackTypeIcon'
import { HeptagonGlyphIcon } from 'components/common/GlyphIcon'
import { HaltButton } from 'components/navigation/Notifications/NotificationsList/HaltButton'
import { ActiveAttackStatusText } from 'components/navigation/Notifications/NotificationsList/ListItems/ActiveAttackStatusText'
import { NOTIFICATION_LEFT_PADDING } from 'components/navigation/Notifications/utils'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { ActiveK8sAttackWithInfraAttack } from 'types/attacks/kubernetes'
import { getFormattedAttackType } from 'utils/metadata'

// you might say "this is almost exactly like `ActiveAttackListItem`. why not just reuse that?"
// well, I did, at first, and it presented all sorts of problems typing the code because of the
// difference between the attack data for infra & k8s attacks, and it is NOT worth the fight
// also, once I separated these and created a new unit test suite for it I realized I had missed
// testing that the proper urls for k8s attacks were created. food for thought.
export const ActiveKubernetesAttackListItem: React.FC<{
  attack: ActiveK8sAttackWithInfraAttack
  doFetch(): void
  onConfirmHalt(options: Record<string, any>): void
}> = ({ attack, doFetch, onConfirmHalt, ...rest }) => {
  const { currentTeamId } = useAuth()

  const { infraAttack = {} } = attack
  const hasInfraAttack = Boolean(infraAttack?.guid)
  const args =
    attack?.attack?.impactDefinition?.cliArgs ??
    infraAttack?.infra_command?.commandArgs?.cliArgs ??
    []
  const attackId = hasInfraAttack ? infraAttack.guid : attack.uid
  const stage = hasInfraAttack ? infraAttack.stage : attack.stage
  const isHalting = isInHaltingStage(stage)

  const handleHaltRequest = ({ reason } = { reason: '' }) =>
    onConfirmHalt({
      type: 'kubernetes',
      attack,
      currentTeamId,
      reason,
      runNumber: null
    })

  const link = {
    pathname: `/attacks/kubernetes/${attack.uid}`,
    state: { k8Attack: attack, infraAttack: infraAttack }
  }
  const IconWrapper = HeptagonGlyphIcon
  const commandType = args[0]

  return (
    <ListItem
      data-test-notification-item
      disablePadding
      component="div"
      {...rest}
      secondaryAction={
        <HaltButton
          fetchAttack={doFetch}
          isHalting={isHalting}
          trackingOptions={{ AttackId: attackId }}
          onConfirmRequest={handleHaltRequest}
        />
      }
    >
      <ListItemButton component={Link} to={link}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ pl: NOTIFICATION_LEFT_PADDING, width: '100%' }}
        >
          <IconWrapper
            css={{ marginRight: theme.space[3] }}
            data-test-attack-icon-type="kubernetes"
          >
            <AttackTypeIcon icon={commandType} />
          </IconWrapper>

          <ListItemText
            data-test-notification-text
            primary={getFormattedAttackType(commandType)}
            secondary={
              <ActiveAttackStatusText
                attackStage={stage}
                isHalting={isHalting}
              />
            }
          />
        </Stack>
      </ListItemButton>
    </ListItem>
  )
}
